import { React, useEffect, useState } from "react";
import CustomInput from "../components/CustomInput";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useFormik } from "formik";
import {
  createImageFeed,
  getAImageFeed,
  resetState,
  updateAImageFeed,
} from "../features/imageFeed/imageFeedSlice";
import Dropzone from "react-dropzone";
import ImageCropperModal from "../components/ImageCropperModal";
import { resolveMediaUrl } from "../utils/mediaUrl";

let schema = yup.object().shape({
  link: yup.string().url("Enter a valid URL").required("Link is Required"),
  feed_image: yup.mixed().required("Image is Required"),
});

const AddImageFeed = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const getFeedId = location.pathname.split("/")[3];
  const navigate = useNavigate();
  const feedImage = useSelector((state) => state.imageFeed.feedImage);
  const feedLink = useSelector((state) => state.imageFeed.feedLink);
  const feedStatus = useSelector((state) => state.imageFeed.feedStatus);
  const feedOrder = useSelector((state) => state.imageFeed.feedOrder);
  const {
    isSuccess,
    isError,
    createdImageFeed,
    updatedImageFeed,
  } = useSelector((state) => state.imageFeed);

  const [img, setImg] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);

  useEffect(() => {
    if (isSuccess && createdImageFeed) {
      toast.success("Image feed item added successfully!");
      navigate("/admin/list-image-feed");
      dispatch(resetState());
    }
    if (isSuccess && updatedImageFeed) {
      toast.success("Image feed item updated successfully!");
      navigate("/admin/list-image-feed");
      dispatch(resetState());
    }
    if (isError) {
      toast.error("Something went wrong!");
    }
  }, [isSuccess, isError, createdImageFeed, updatedImageFeed, navigate, dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      link: feedLink || "",
      feed_image: feedImage || "",
      status: feedStatus || "Show",
      order: feedOrder ?? 0,
    },
    validationSchema: schema,
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("link", values.link);
      formData.append("status", values.status);
      formData.append("order", values.order);

      if (img instanceof File) {
        formData.append("feed_image", img);
      } else if (feedImage && typeof feedImage === "string") {
        formData.append("feed_image", feedImage);
      } else {
        formData.append("feed_image", "");
      }

      if (getFeedId) {
        dispatch(updateAImageFeed({ id: getFeedId, feedData: formData }));
      } else {
        dispatch(createImageFeed(formData));
      }
    },
  });

  useEffect(() => {
    if (getFeedId) {
      dispatch(getAImageFeed(getFeedId));
    } else {
      dispatch(resetState());
    }
  }, [getFeedId, dispatch]);

  return (
    <div>
      <h3 className="mb-4 title">
        {getFeedId ? "Edit" : "Add"} Image Feed
      </h3>
      <div>
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-3">
            <label className="form-label" htmlFor="link">
              Link <span className="text-danger">*</span>
            </label>
            <CustomInput
              type="url"
              label="Enter link (e.g. Instagram or product URL)"
              onChng={formik.handleChange("link")}
              onBlr={formik.handleBlur("link")}
              val={formik.values.link}
              id="link"
            />
            <div className="error">
              {formik.touched.link && formik.errors.link}
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="status">
              Status
            </label>
            <select
              id="status"
              name="status"
              className="form-control"
              value={formik.values.status}
              onChange={formik.handleChange}
            >
              <option value="Show">Show</option>
              <option value="Hide">Hide</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label" htmlFor="order">
              Display order
            </label>
            <CustomInput
              type="number"
              label="Order (lower numbers appear first)"
              onChng={formik.handleChange("order")}
              onBlr={formik.handleBlur("order")}
              val={formik.values.order}
              id="order"
            />
          </div>

          <div>
            <label className="form-label" htmlFor="feed_image">
              Feed Image <span className="text-danger">*</span>
            </label>
            <div className="bg-white border-1 p-5 text-center mt-1">
              <Dropzone
                onDrop={(acceptedFiles) => {
                  if (acceptedFiles.length > 0) {
                    setPendingFile(acceptedFiles[0]);
                    setCropOpen(true);
                  }
                }}
                multiple={false}
              >
                {({ getRootProps, getInputProps }) => (
                  <section>
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <p>Drag &apos;n&apos; drop an image here, or click to select</p>
                    </div>
                  </section>
                )}
              </Dropzone>
            </div>
            <div className="showimages d-flex flex-wrap gap-3 mt-3">
              {formik.values.feed_image && (
                <div className="position-relative">
                  <button
                    type="button"
                    onClick={() => {
                      setImg(null);
                      formik.setFieldValue("feed_image", "");
                    }}
                    className="position-absolute"
                    style={{
                      right: "5px",
                      background: "transparent",
                      border: "none",
                      color: "white",
                      fontSize: "1.5rem",
                      fontWeight: "bold",
                    }}
                  >
                    ×
                  </button>
                  {typeof formik.values.feed_image === "string" ? (
                    <img
                      src={resolveMediaUrl(formik.values.feed_image)}
                      alt="feed"
                      width={200}
                      height={200}
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <img
                      src={URL.createObjectURL(formik.values.feed_image)}
                      alt="feed"
                      width={200}
                      height={200}
                      style={{ objectFit: "cover" }}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="error">
              {formik.touched.feed_image && formik.errors.feed_image}
            </div>
          </div>

          <button
            className="btn btn-success border-0 rounded-3 my-5"
            type="submit"
          >
            {getFeedId ? "Update" : "Add"} Image Feed
          </button>
        </form>
      </div>

      <ImageCropperModal
        open={cropOpen}
        file={pendingFile}
        title="Crop image feed photo"
        aspect={1}
        outputWidth={800}
        outputHeight={800}
        onCancel={() => {
          setCropOpen(false);
          setPendingFile(null);
        }}
        onCropped={(cropped) => {
          setImg(cropped);
          formik.setFieldValue("feed_image", cropped);
          setCropOpen(false);
          setPendingFile(null);
        }}
      />
    </div>
  );
};

export default AddImageFeed;
